/**
 * View As — the pure rules (Beta-1 plan P4-03 / P4-04 / P4-08, Tim C0814 "you can only see down,
 * not up"). No Firebase; DemoRoleContext and RoleSwitcher consume it.
 */
import { roleRank } from './roleHierarchy.js';

/** Menu order (P4-03): Administrator first, Org IT next to last, PPI Tech Support last (superadmins). */
export const VIEW_AS_ROLE_ORDER = Object.freeze(['org_admin', 'executive', 'site_manager', 'department_lead', 'team_lead', 'respondent', 'org_it']);

/** "Sinclair, Dean" ordering key: stored lastName, else the last word of the display name. */
export function lastNameOf(m) {
  if (m?.lastName) return String(m.lastName).trim().toLowerCase();
  const name = String(m?.displayName || '').trim();
  if (!name) return String(m?.email || '').toLowerCase();
  const parts = name.split(/\s+/);
  return parts[parts.length - 1].toLowerCase();
}

export function firstNameOf(m) {
  if (m?.firstName) return String(m.firstName).trim().toLowerCase();
  const name = String(m?.displayName || '').trim();
  return name.split(/\s+/)[0]?.toLowerCase() || '';
}

export function compareByLastName(a, b) {
  return lastNameOf(a).localeCompare(lastNameOf(b)) || firstNameOf(a).localeCompare(firstNameOf(b)) || String(a.email || '').localeCompare(String(b.email || ''));
}

/** Resolve a member's units from their role scope (+ stored nwtId/deptId/siteId when present). */
export function memberUnits(member, org) {
  const departments = org?.departments || [];
  const teams = org?.teams || [];
  let nwtId = member?.nwtId || null;
  let deptId = member?.deptId || null;
  let siteId = member?.siteId || null;
  const type = member?.scopeType;
  const id = member?.scopeId;
  if (type === 'team' && id) nwtId = nwtId || id;
  if (type === 'department' && id) deptId = deptId || id;
  if (type === 'site' && id) siteId = siteId || id;
  if (nwtId && !deptId) deptId = teams.find((t) => t.id === nwtId)?.departmentId || null;
  if (deptId && !siteId) siteId = departments.find((d) => d.id === deptId)?.siteId || null;
  return { nwtId, deptId, siteId };
}

/**
 * Downward visibility (P4-04): which members may `viewer` view as?
 *   viewer = { uid, role, scopeId, scopeType, nwtId?, deptId?, siteId?, isSuperadmin? }
 * org admin / Org IT / superadmin → everyone; executive → site managers and below;
 * site manager → DH/FLS/IC in their site; DH → FLS/IC in their department; FLS → ICs in their NWT;
 * an individual contributor sees nobody. Never yourself, never anyone at or above your rank.
 */
export function visibleMembersFor(viewer, members, org) {
  if (!viewer) return [];
  const list = (members || []).filter((m) => m.uid !== viewer.uid);
  if (viewer.isSuperadmin || viewer.role === 'org_admin' || viewer.role === 'org_it') return list;
  const myRank = roleRank(viewer.role);
  if (myRank <= roleRank('respondent')) return [];
  const mine = memberUnits(viewer, org);
  return list.filter((m) => {
    if (roleRank(m.role) >= myRank) return false;
    if (m.role === 'org_it' || m.role === 'org_admin') return false;
    if (viewer.role === 'executive') return true;
    const u = memberUnits(m, org);
    if (viewer.role === 'site_manager') return Boolean(mine.siteId) && u.siteId === mine.siteId;
    if (viewer.role === 'department_lead') return Boolean(mine.deptId) && u.deptId === mine.deptId;
    if (viewer.role === 'team_lead') return Boolean(mine.nwtId) && u.nwtId === mine.nwtId;
    return false;
  });
}

/** Can this viewer open View As at all? (P4-04: everyone above individual contributor) */
export function canViewAsRole(role, isSuperadmin = false) {
  return Boolean(isSuperadmin) || roleRank(role) > roleRank('respondent');
}

/** Generic lenses a viewer may pick: their own role and every role below it. */
export function lensRolesFor(viewer) {
  if (!viewer) return [];
  if (viewer.isSuperadmin || viewer.role === 'org_admin' || viewer.role === 'org_it') return [...VIEW_AS_ROLE_ORDER];
  const myRank = roleRank(viewer.role);
  return VIEW_AS_ROLE_ORDER.filter((r) => r !== 'org_it' && roleRank(r) <= myRank);
}

/** Case-insensitive "Sin" → Sinclair; matches first/last/display name, email and title. */
export function filterMembers(members, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return members;
  return members.filter((m) =>
    [m.firstName, m.lastName, m.displayName, m.email, m.title].some((v) => v && String(v).toLowerCase().includes(q))
  );
}

/** Group by role in menu order, each group sorted by last name: [{ role, members }]. */
export function groupMembersByRole(members, roles = VIEW_AS_ROLE_ORDER) {
  return roles.map((role) => ({ role, members: members.filter((m) => m.role === role).sort(compareByLastName) }));
}
